<template>
  <div :class="$style.selectWrapper">
    <select
      v-model="localValue"
      :disabled="disabled"
      :class="[$style.select, { [$style.disabled]: disabled }, { [$style.readonly]: readonly }]"
    >
      <option v-if="placeholder" :value="placeholderComputedValue" selected disabled :class="$style.placeholder">{{
        placeholder
      }}</option>
      <option v-for="option in options" :key="(getValue(option) as string)" :value="getValue(option)">
        {{ getLabel(option) }}
      </option>
    </select>
  </div>
</template>

<script lang="ts" setup>
import { computed } from "vue"

const props = withDefaults(
  defineProps<{
    /** 選択肢リスト */
    options:
      | Record<string, unknown>[]
      | string[]
      | number[]
      | readonly string[]
      | readonly number[]
      | readonly Record<string, unknown>[]
    /** 選択中の値 */
    modelValue?: string | number | Record<string, unknown> | null
    /** 疑似プレースホルダー */
    placeholder?: string
    /** 疑似プレースホルダーの値 */
    placeholderValue?: string | number | null
    /** ラベルに使用するoptionsのkey */
    optionLabel?: string
    /** 値に使用するoptionsのkey */
    optionValue?: string
    /** 非活性 */
    disabled?: boolean
    readonly?: boolean
  }>(),
  {
    modelValue: null,
    optionLabel: "label",
    optionValue: "value",
    placeholder: "",
    placeholderValue: null,
    disabled: false,
    readonly: false,
  },
)

/**
 * 選択肢がオブジェクトの配列かどうか
 */
const isRecordArray = computed(() => {
  return props.options.length > 0 && typeof props.options[0] === "object"
})

const getValue = (option: string | number | Record<string, unknown>) => {
  if (isRecordArray.value) {
    return props.optionValue ? (option as Record<string, unknown>)[props.optionValue] : option
  }
  return option
}

const getLabel = (option: string | number | Record<string, unknown>) => {
  if (isRecordArray.value) {
    return props.optionLabel ? (option as Record<string, unknown>)[props.optionLabel] : option
  }
  return option
}

const emit = defineEmits<{
  (e: "update:modelValue", modelValue: string | number | Record<string, unknown> | null): void
}>()

const localValue = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
})

const placeholderComputedValue = computed(() => {
  return props.placeholderValue === null ? (typeof props.modelValue === "number" ? 0 : "") : props.placeholderValue
})
</script>

<style scoped module lang="scss">
.selectWrapper {
  width: 100%;
  position: relative;
  // 下矢印
  &::before {
    content: "";
    position: absolute;
    top: 40%;
    right: 12px;
    transform: translateY(-50%);
    pointer-events: none;
    color: $secondary-100;
    line-height: 1;
    width: 0.5rem;
    height: 0.5rem;
    border-right: 0.1em solid currentColor;
    border-top: 0.1em solid currentColor;
    box-sizing: border-box;
    transform: translateY(-25%) rotate(135deg);
  }
}
.select {
  width: 100%;
  -webkit-appearance: none;
  appearance: none;
  font-size: $font-size-16;
  cursor: pointer;
  color: $primary;
  padding: 0.75rem 1rem;
  background-color: $secondary-10;
  border: 1px solid $primary-40;
  border-radius: 0.25rem;
  transition: all 0.2s ease;
  &:hover {
    background-color: $primary-20;
  }
  &:focus {
    background-color: $white;
    border: 1px solid $primary;
    box-shadow: 0 0 0 0.25rem $primary-20;
  }
  &::-ms-expand {
    display: none;
  }
  &.disabled {
    color: $black-40;
    padding: 0.75rem 1rem;
    background-color: $black-5;
    border: 1px solid $black-20;
    border-radius: 0.25rem;
    cursor: not-allowed;
    pointer-events: none;
  }
  &.readonly {
    color: $black-60;
    padding: 0.75rem 1rem;
    background-color: $white;
    border: 1px solid $black-20;
    border-radius: 0.25rem;
    cursor: not-allowed;
    pointer-events: none;
  }
  option.placeholder {
    display: none;
    opacity: 0.5;
  }
}
</style>
